import { dueDay, dueTone, isDone, nextMilestone, projectProgress, type MilestoneLike, type PhaseLike } from "@shared/clientProgress";
import type { OfferLine } from "@shared/offerMath";
import { fmtUsd } from "@/hooks/useFinancials";
import type { Fact } from "../FactsStrip";
import { dayLabel, inlineDayLabel, monthLabel } from "../recordFormat";

/** The buyer tracker's funnel totals (the aggregates of GET /api/companies/:id/buyer-tracker). */
export interface FunnelCounts { teasers: number; ndas: number; cims: number; iois: number; lois: number; total: number; passed: number }

export interface ClientFactsInput {
  today: string; // YYYY-MM-DD in Central
  status: string | null;
  project: { phases: PhaseLike[]; milestones: MilestoneLike[] } | null;
  aggregates: FunnelCounts | null;
  best: OfferLine | null;
  expectedCloseOn: string | null;
  closing: { outcome: string; closedOn: string; feeCollected: string | null } | null;
  /** From /api/financials/fee-detail. undefined: no financials grant, so no fee fact. null: granted, nothing loaded. */
  fee?: { projectedFee: number | null; successFee: number | null } | null;
}

function phaseFact(project: ClientFactsInput["project"]): Fact {
  if (!project) return { label: "Phase", value: "No project" };
  const p = projectProgress(project.phases, project.milestones);
  if (p.currentIndex < 0 && project.phases.length) return { label: "Phase", value: "All phases complete", sub: `${p.overall}% overall` };
  return { label: "Phase", value: p.current?.name ?? "No phases", sub: `${p.overall}% overall` };
}

function nextMilestoneFact(project: ClientFactsInput["project"], today: string): Fact {
  const label = "Next milestone";
  if (!project) return { label, value: "No project" };
  if (!project.milestones.length) return { label, value: "None scheduled" };
  const open = project.milestones.filter((m) => !isDone(m));
  if (!open.length) return { label, value: "All done" };
  const next = nextMilestone(project.milestones);
  const day = next ? dueDay(next.dueDate) : null;
  if (!next || !day) return { label, value: "None scheduled", sub: `${open.length} without a date` };
  const tone = dueTone(day, today);
  const when = day === today ? "due today" : day < today ? `was due ${inlineDayLabel(day, today)}` : `due ${inlineDayLabel(day, today)}`;
  return { label, value: next.name, sub: when, ...(tone ? { tone } : {}) };
}

function buyersFact(a: FunnelCounts | null): Fact {
  if (!a || a.total === 0) return { label: "Buyers", value: "None yet" };
  return { label: "Buyers", value: `${a.total} on tracker`, sub: `${a.ndas} NDA · ${a.cims} CIM · ${a.iois} IOI · ${a.lois} LOI` };
}

function bestOfferFact(best: OfferLine | null): Fact {
  if (!best) return { label: "Best offer", value: "None yet" };
  return { label: "Best offer", value: best.multiple, sub: `${best.stage} · ${best.buyerName} · ${best.ev}` };
}

/** A closed engagement shows how it ended; otherwise the target close month (expected_close_on). */
function closeFact(i: ClientFactsInput): Fact {
  if (i.status === "Closed") {
    if (!i.closing) return { label: "Closed", value: "Outcome not recorded", tone: "warn" };
    return { label: "Closed", value: i.closing.outcome, sub: dayLabel(i.closing.closedOn, i.today) };
  }
  if (!i.expectedCloseOn) return { label: "Target close", value: "Not set" };
  return i.expectedCloseOn.slice(0, 7) < i.today.slice(0, 7)
    ? { label: "Target close", value: monthLabel(i.expectedCloseOn), sub: "month has passed", tone: "warn" }
    : { label: "Target close", value: monthLabel(i.expectedCloseOn) };
}

/** Only with the grant. A live engagement shows what is still to come; a closed one, what was collected. */
function feeFact(i: ClientFactsInput): Fact | null {
  if (i.fee === undefined) return null;
  if (i.status === "Closed") {
    const outcome = i.closing?.outcome;
    // Only a sale carries a fee.
    if (outcome === "No Deal" || outcome === "Withdrawn") return { label: "Fee collected", value: "None", sub: outcome, gated: true };
    const f = i.closing?.feeCollected;
    return { label: "Fee collected", value: f ? fmtUsd(Number(f)) : "Not recorded", gated: true };
  }
  const p = i.fee?.projectedFee;
  if (p == null) return { label: "Projected fee", value: "Not set", sub: "needs deal value and fee %", gated: true };
  const s = i.fee?.successFee;
  return { label: "Projected fee", value: fmtUsd(p), sub: s != null ? `of ${fmtUsd(s)} success fee` : null, gated: true };
}

/** The Client page's facts: phase, next milestone, buyers, best offer, close, and (granted only) the fee. */
export function clientFacts(i: ClientFactsInput): Fact[] {
  const facts = [phaseFact(i.project), nextMilestoneFact(i.project, i.today), buyersFact(i.aggregates), bestOfferFact(i.best), closeFact(i)];
  const fee = feeFact(i);
  return fee ? [...facts, fee] : facts;
}
